/* eslint-disable react/prop-types */
// React Dependencies
import React, { useState, forwardRef, useImperativeHandle } from 'react';

// Extenral Dependencies
import { Button, Dialog, DialogTitle, DialogActions, DialogContent, DialogContentText, useTheme, useMediaQuery } from '@material-ui/core';
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';

// Internal Dependencies
import { IsEmpty } from '../../../common/checks';
import SortableCardItem from './sortableCardItem';

const SortableCard = forwardRef(({ cards, onEvent, onDelete, onUpdatePosType }, ref) => {
  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 500,
      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        delay: 500,
      },
    }),
  );

  const [items, setItems] = useState(cards);
  const [open, setOpenDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [disableBtn, setDisableBtn] = useState(false);
  const [activeCardId, setActiveCardId] = useState(null);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const onChange = (postId, positionType) => {
    const payload = {
      postId,
      positionType,
    };

    onUpdatePosType(payload);
  };

  const onClickRemove = (postId) => {
    setItemToDelete(postId);
    setOpenDialog(true);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (IsEmpty(active) || IsEmpty(over)) {
      return;
    }

    if (active.id !== over.id) {
      setItems((_items) => {
        const oldIndex = _items.findIndex((item) => item.id === active.id);
        const newIndex = _items.findIndex((item) => item.id === over.id);

        const oldData = [...items];
        const sorted = arrayMove(_items, oldIndex, newIndex);

        onEvent({ name: 'move', data: { old: oldData, new: sorted } });

        return sorted;
      });
    }

    setActiveCardId(null);
  };

  const handleDragStart = (event) => {
    setActiveCardId(event.active.id);
  };

  useImperativeHandle(ref, () => ({
    updateItems(data) {
      setItems(data);
    },
  }));

  const handleCloseRemoveDialog = () => {
    setItemToDelete(null);
    setOpenDialog(false);
  };

  const onConfirmRemove = async () => {
    setDisableBtn(true);
    await onDelete(itemToDelete);

    if (items.length <= 1) {
      setItems([]);
    }

    setOpenDialog(false);
    setDisableBtn(false);
  };

  return items.length > 0 ? (
    <>
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} sensors={sensors}>
        <SortableContext
          items={items}
          strategy={rectSortingStrategy}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {items.map((value) => (
              <SortableCardItem
                key={`pinPost-${value.id}`}
                active={value.id === activeCardId}
                cardItem={value}
                onChange={onChange}
                onClickRemove={onClickRemove}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <Dialog
        fullScreen={fullScreen}
        open={open}
        onClose={handleCloseRemoveDialog}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle id="responsive-dialog-title">Remove Pinned Post</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure want to remove this pinned post?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => onConfirmRemove()}
            color="secondary"
            variant="contained"
            disabled={disableBtn}
          >
            Yes
          </Button>
          <Button autoFocus onClick={() => handleCloseRemoveDialog()} color="default">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : <div align="center"><h5>No Data</h5></div>;
});

export default SortableCard;
